import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { api } from './client';
import type { Agenda, AgendaListResponse } from '../types/agenda';

export const qk = {
  agendas: ['agendas'] as const,
  agenda: (id: string) => ['agenda', id] as const,
  ghostStatus: ['ghost-status'] as const,
  emoji: ['emoji'] as const,
  tenants: ['tenants'] as const,
};

export function useTenants() {
  return useQuery({ queryKey: qk.tenants, queryFn: api.listTenants });
}

export function useAgendas() {
  return useQuery({ queryKey: qk.agendas, queryFn: api.listAgendas });
}

export function useAgenda(id: string, options?: Partial<UseQueryOptions<Agenda>>) {
  return useQuery({
    queryKey: qk.agenda(id),
    queryFn: () => api.getAgenda(id),
    // Always mount against current server state, never a leftover cache entry.
    // The editor seeds its draft from this data exactly once (EditorProvider is
    // keyed on id), so a stale cached copy would be shown even while a fresh GET
    // succeeds in the background. gcTime: 0 drops the cache when the screen
    // unmounts and refetchOnMount: 'always' forces a fresh fetch on entry — the
    // price is a brief spinner in exchange for consistently accurate data. The
    // fine-grained autosave endpoints never write back into this cache, so it
    // cannot be trusted to stay in sync with the server on its own.
    gcTime: 0,
    refetchOnMount: 'always',
    ...options,
  });
}

export function useGhostStatus() {
  return useQuery({ queryKey: qk.ghostStatus, queryFn: api.ghostStatus });
}

export function useEmoji() {
  return useQuery({ queryKey: qk.emoji, queryFn: api.listEmoji, staleTime: Infinity });
}

export function useCreateAgenda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (agenda: Agenda) => api.createAgenda(agenda),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.agendas }),
  });
}

export function useUpdateAgenda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (agenda: Agenda) => api.updateAgenda(agenda),
    onSuccess: (saved) => {
      qc.setQueryData(qk.agenda(saved.id), saved);
      qc.invalidateQueries({ queryKey: qk.agendas });
    },
  });
}

export function useDeleteAgenda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteAgenda(id),
    onMutate: (id) => {
      qc.setQueryData<AgendaListResponse>(qk.agendas, (prev) =>
        prev ? { ...prev, agendas: Array.isArray(prev.agendas) ? prev.agendas.filter((s) => s.id !== id) : [] } : prev,
      );
    },
    onSettled: () => qc.invalidateQueries({ queryKey: qk.agendas }),
  });
}
